/**
 * Rock fragments after a fracture (M2 plan §6 M2-16, spec §5.4): the `FragmentPool` view the presenters read, plus
 * the runtime-only columns (group, collider handle) and the table of fractured parents ("groups"). A group holds
 * one parent's ore mix, resistance and static collider set: the fragments of one parent are static balls on one
 * fixed body anchored at the parent's float64 centre (`addStaticBalls`, `EID_ASTEROID`), so the float32 offsets stay
 * small (D-011, D-013). Fragments regenerate from `(parent, k)` (`fragmentSpec`), so a save stores only the parent
 * id and each live fragment's remaining tonnes.
 *
 * Fragments always CHIP (their chunks are `chip.fragmentYieldMult` larger); a fragment is never fractured again.
 * Spawning and removal change Rapier colliders (rare events; Rapier's binding allocates, D-024); everything else
 * here is allocation-free (D-014).
 */
import { createFragmentSpec, fragmentCount, fragmentSpec, type FragmentSpec } from '../mining/composition'
import { WARE_COUNT, type MiningConfig } from '../data/m2Schemas'
import { EID_ASTEROID, type Physics } from '../physics/physics'
import { addStaticBalls, NO_HANDLE, removeStaticCollider, removeStaticColliders, type StaticColliders } from '../physics/statics'
import { MINING_MODE, type FragmentPool } from './contracts'
import { seededUnit } from './hash'

/** Hash salt of the fragments' shader seeds (+ k). */
const SEED_SALT_FRAGMENT = 0xf3c0

/** Fractured parents with live fragments, plus the runtime columns of the fragment pool. */
export interface FragmentState {
  readonly pool: FragmentPool
  /** Group of each fragment slot (-1 = none). */
  readonly group: Int16Array
  /** Collider handle of each fragment slot (`NO_HANDLE` = none). */
  readonly handle: Int32Array
  /** Group table (capacity = the pool's: a group has at least one live fragment). */
  readonly groupAlive: Uint8Array
  readonly groupParent: Int32Array
  /** Live fragments of the group. */
  readonly groupLive: Uint8Array
  /** The parent's ore mix, `WARE_COUNT` per group. */
  readonly groupMix: Float32Array
  readonly groupResistance: Float32Array
  readonly groupTier: Uint8Array
  readonly groupColliders: (StaticColliders | null)[]
  /** Scratch per fragment count n (1..max): n × 3 centres and n radii, the exact lengths `addStaticBalls` wants. */
  readonly scratchXyz: Float64Array[]
  readonly scratchRadii: Float32Array[]
  /** Pool slot of each spawned k in the current spawn (scratch). */
  readonly scratchSlot: Int32Array
  readonly spec: FragmentSpec
}

export function createFragmentState(pool: FragmentPool, maxFragments: number): FragmentState {
  const cap = pool.capacity
  const scratchXyz: Float64Array[] = []
  const scratchRadii: Float32Array[] = []
  for (let n = 0; n <= maxFragments; n++) {
    scratchXyz.push(new Float64Array(n * 3))
    scratchRadii.push(new Float32Array(n))
  }
  return {
    pool,
    group: new Int16Array(cap).fill(-1),
    handle: new Int32Array(cap).fill(NO_HANDLE),
    groupAlive: new Uint8Array(cap),
    groupParent: new Int32Array(cap).fill(-1),
    groupLive: new Uint8Array(cap),
    groupMix: new Float32Array(cap * WARE_COUNT),
    groupResistance: new Float32Array(cap),
    groupTier: new Uint8Array(cap),
    groupColliders: new Array<StaticColliders | null>(cap).fill(null),
    scratchXyz,
    scratchRadii,
    scratchSlot: new Int32Array(maxFragments),
    spec: createFragmentSpec(),
  }
}

/** Stable key of fragment `k` of `parent` for the scanner's Mk II error stream (never collides with a rock id). */
export function fragmentKey(parent: number, k: number): number {
  return (0x40000000 | (parent << 4) | (k & 15)) >>> 0
}

/** The parent a fracture splits (caller-owned scratch). */
export interface FragmentParent {
  id: number
  x: number
  y: number
  z: number
  radius: number
  massT: number
  archetype: number
  resistance: number
  tier: number
}

export function createFragmentParent(): FragmentParent {
  return { id: -1, x: 0, y: 0, z: 0, radius: 0, massT: 0, archetype: 0, resistance: 0, tier: 0 }
}

function freeGroupSlot(st: FragmentState): number {
  const n = st.groupAlive.length
  for (let g = 0; g < n; g++) if (st.groupAlive[g] === 0) return g
  return -1
}

function freeFragmentSlot(pool: FragmentPool): number {
  for (let i = 0; i < pool.high; i++) if (pool.alive[i] === 0) return i
  return pool.high < pool.capacity ? pool.high : -1
}

/**
 * Splits `parent` into its seeded fragments (`fragmentCount`, `fragmentSpec`): writes the pool slots, builds the
 * static balls (radius × `colliderScale`, like the field) and the group. `keep` (import only) holds the remaining
 * tonnes per k (a negative value = that fragment is gone); null = every fragment at full mass. Returns how many
 * fragments were spawned (fewer when the pool is full; 0 when no group slot is free).
 */
export function spawnFragments(
  st: FragmentState,
  physics: Physics,
  parent: Readonly<FragmentParent>,
  mix: Float32Array,
  mixOffset: number,
  fieldSeed: number,
  cfg: MiningConfig,
  colliderScale: number,
  keep: Float32Array | null,
): number {
  const g = freeGroupSlot(st)
  if (g < 0) return 0
  const pool = st.pool
  const count = fragmentCount(parent.id, fieldSeed, cfg)
  const spec = st.spec
  let n = 0
  for (let k = 0; k < count; k++) {
    if (keep !== null && !(keep[k] > 0)) continue
    const slot = freeFragmentSlot(pool)
    if (slot < 0) break
    fragmentSpec(parent.id, fieldSeed, k, count, parent.radius, parent.massT, cfg, spec)
    pool.alive[slot] = 1
    if (slot >= pool.high) pool.high = slot + 1
    pool.live++
    pool.parent[slot] = parent.id
    pool.k[slot] = k
    pool.x[slot] = parent.x + spec.ox
    pool.y[slot] = parent.y + spec.oy
    pool.z[slot] = parent.z + spec.oz
    pool.radius[slot] = spec.radius
    pool.archetype[slot] = parent.archetype
    pool.seed[slot] = seededUnit(fieldSeed, parent.id, SEED_SALT_FRAGMENT + k)
    pool.massT[slot] = spec.massT
    pool.remainingT[slot] = keep !== null && keep[k] < spec.massT ? keep[k] : spec.massT
    pool.mode[slot] = MINING_MODE.chip
    st.group[slot] = g
    st.scratchSlot[n] = slot
    n++
  }
  if (n === 0) return 0
  const xyz = st.scratchXyz[n]
  const radii = st.scratchRadii[n]
  for (let j = 0; j < n; j++) {
    const slot = st.scratchSlot[j]
    xyz[j * 3] = pool.x[slot]
    xyz[j * 3 + 1] = pool.y[slot]
    xyz[j * 3 + 2] = pool.z[slot]
    radii[j] = pool.radius[slot] * colliderScale
  }
  const set = addStaticBalls(physics, parent.x, parent.y, parent.z, xyz, radii, EID_ASTEROID)
  for (let j = 0; j < n; j++) st.handle[st.scratchSlot[j]] = set.handles[j]
  st.groupAlive[g] = 1
  st.groupParent[g] = parent.id
  st.groupLive[g] = n
  for (let w = 0; w < WARE_COUNT; w++) st.groupMix[g * WARE_COUNT + w] = mix[mixOffset + w]
  st.groupResistance[g] = parent.resistance
  st.groupTier[g] = parent.tier
  st.groupColliders[g] = set
  return n
}

/** Removes fragment `slot` (depleted): its collider, and the group's body once its last fragment is gone. */
export function removeFragment(st: FragmentState, physics: Physics, slot: number): void {
  const pool = st.pool
  if (slot < 0 || slot >= pool.high || pool.alive[slot] === 0) return
  const g = st.group[slot]
  const set = g >= 0 ? st.groupColliders[g] : null
  if (set !== null) removeStaticCollider(physics, set, st.handle[slot])
  pool.alive[slot] = 0
  pool.live--
  pool.remainingT[slot] = 0
  st.handle[slot] = NO_HANDLE
  st.group[slot] = -1
  while (pool.high > 0 && pool.alive[pool.high - 1] === 0) pool.high--
  if (g < 0) return
  st.groupLive[g]--
  if (st.groupLive[g] === 0) {
    if (set !== null) removeStaticColliders(physics, set)
    st.groupColliders[g] = null
    st.groupAlive[g] = 0
    st.groupParent[g] = -1
  }
}

/** Removes every fragment and group (new career, load). */
export function clearFragments(st: FragmentState, physics: Physics): void {
  const pool = st.pool
  for (let g = 0; g < st.groupAlive.length; g++) {
    const set = st.groupColliders[g]
    if (set !== null) removeStaticColliders(physics, set)
    st.groupColliders[g] = null
    st.groupAlive[g] = 0
    st.groupParent[g] = -1
    st.groupLive[g] = 0
  }
  for (let i = 0; i < pool.capacity; i++) {
    pool.alive[i] = 0
    pool.remainingT[i] = 0
    st.group[i] = -1
    st.handle[i] = NO_HANDLE
  }
  pool.high = 0
  pool.live = 0
}

/** Offset of fragment `slot`'s ore mix in `groupMix` (its group's), or -1. */
export function fragmentMixOffset(st: FragmentState, slot: number): number {
  const g = st.group[slot]
  return g >= 0 ? g * WARE_COUNT : -1
}
